import { APP } from 'config';
import { Env, FeatureKey, FeatureToggleManager } from './Types';

const FEATURES_KEY = 'mfts';
const ENV_KEY = 'mft-env';

interface FeatureToggleConfig {
  isEnabled: boolean;
}

export class LocalStorageFTManager implements FeatureToggleManager {
  constructor(private features: Record<FeatureKey, FeatureToggleConfig>) {}

  getEnabledFeatureKeys() {
    return JSON.parse(
      localStorage.getItem(FEATURES_KEY) ?? '[]',
    ) as FeatureKey[];
  }

  private setEnabledKeys(keys: FeatureKey[]) {
    localStorage.setItem(FEATURES_KEY, JSON.stringify(keys));
  }

  getFeaturesArray = () => {
    const featureKeys = Object.keys(this.features) as FeatureKey[];
    return featureKeys.map((key) => ({
      key,
      isEnabled: this.features[key].isEnabled,
    }));
  };

  isFeatureEnabled = (key: FeatureKey) => {
    const env = this.getEnv();
    if ([Env.development, Env.uat].includes(env)) {
      return true;
    }
    const defaultConfig = this.getFeaturesArray().find(
      (feature) => feature.key === key,
    );
    const enabledKeys = this.getEnabledFeatureKeys();
    return (enabledKeys?.includes(key) || defaultConfig?.isEnabled) ?? false;
  };

  enableFeature = (key: FeatureKey) => {
    const currentKeys = this.getEnabledFeatureKeys();
    if (!currentKeys.includes(key)) {
      this.setEnabledKeys([...currentKeys, key]);
    }
  };

  disableFeature = (key: FeatureKey) => {
    const currentKeys = this.getEnabledFeatureKeys();
    if (currentKeys.includes(key)) {
      const newKeys = currentKeys.filter((currentKey) => currentKey === key);
      this.setEnabledKeys(newKeys);
    }
  };

  availableFeatures = () => {
    return this.getFeaturesArray().map((feature) => feature.key);
  };

  private getEnv = () => {
    const persistedEnv = localStorage.getItem(ENV_KEY);
    if (!persistedEnv) {
      if (process.env.NODE_ENV === 'development') {
        return Env.development;
      }
      if (APP.env === 'uat') {
        return Env.uat;
      }
      return Env.production;
    }
    return persistedEnv as Env;
  };

  setEnv = (envKey: Env) => {
    localStorage.setItem(ENV_KEY, envKey);
  };

  disableAll = () => {
    localStorage.setItem(FEATURES_KEY, '[]');
  };
}
